import React, { useState, useEffect, useCallback, useRef } from "react";
import { Form, Modal } from "react-bootstrap";
import { Toaster, toast } from "react-hot-toast";

import Pagination from "../components/Pagination";
import { generatePurchaseLedgerPdfBlob } from "../utils/generatePurchaseLedgerPdf";
import { useButtonTableStyle } from "../utils/useButtonTableStyle";
import { parseApiPagination } from "../utils/parseApiPagination";
import {
  postPurchaseLedger,
  isErpApiSuccess,
  getErpRequestErrorMessage,
} from "../erpClient";

function processPurchaseLedgerEntries(entries) {
  return entries.map((entry) => {
    // Format date from YYYY-MM-DD to DD/MM/YYYY
    let formattedDate =
      entry.date || entry.purchase_date || entry.bill_date || "-";
    if (formattedDate !== "-" && formattedDate.includes("-")) {
      const dateParts = formattedDate.split(" ")[0].split("-");
      if (dateParts.length === 3) {
        formattedDate = `${dateParts[2]}/${dateParts[1]}/${dateParts[0]}`;
      }
    }

    // Format name: company_name (name) if name exists, otherwise just company_name
    let displayName = entry.company_name || "-";
    if (entry.name && entry.name.trim() && entry.company_name) {
      displayName = `${entry.company_name} (${entry.name})`;
    } else if (entry.company_name) {
      displayName = entry.company_name;
    } else if (entry.name) {
      displayName = entry.name;
    }

    return {
      name: displayName,
      phone: entry.contact_number || entry.phone || entry.mobile || "-",
      purchase_no: entry.purchase_no || entry.voucher_number || "-",
      purchase_date: formattedDate,
      purchase_value: parseFloat(
        entry.total_amount || entry.grand_total || 0
      ),
      taxable_value: parseFloat(
        entry.gross_amount || entry.final_amount || entry.taxable_value || 0
      ),
      sgst: parseFloat(entry.sgst || 0),
      cgst: parseFloat(entry.cgst || 0),
      total_tax: parseFloat(
        entry.gst_amount || entry.total_tax || entry.gst || 0
      ),
      gold_gm: parseFloat(entry.gold_gm || entry.gold_weight || 0),
      silver_gm: parseFloat(entry.silver_gm || entry.silver_weight || 0),
    };
  });
}

function summarizePurchaseLedgerRows(processedEntries) {
  return processedEntries.reduce(
    (acc, entry) => {
      acc.purchase_value += entry.purchase_value;
      acc.taxable_value += entry.taxable_value;
      acc.sgst += entry.sgst;
      acc.cgst += entry.cgst;
      acc.total_tax += entry.total_tax;
      acc.gold_gm += entry.gold_gm;
      acc.silver_gm += entry.silver_gm;
      return acc;
    },
    {
      purchase_value: 0,
      taxable_value: 0,
      sgst: 0,
      cgst: 0,
      total_tax: 0,
      gold_gm: 0,
      silver_gm: 0,
    }
  );
}

function PurchaseLedger() {
  const { buttonClass, cardClass } = useButtonTableStyle();
  const [loading, setLoading] = useState(false);
  const [printLoading, setPrintLoading] = useState(false);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [entireReport, setEntireReport] = useState(false);
  const [ledgerRows, setLedgerRows] = useState([]);
  const [summaryData, setSummaryData] = useState(null);
  const [showPDFModal, setShowPDFModal] = useState(false);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const pdfUrlRef = useRef(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMorePages, setHasMorePages] = useState(false);
  const ignoreNextPageFetchRef = useRef(false);
  const pageCacheRef = useRef(new Map());

  useEffect(() => {
    return () => {
      if (pdfUrlRef.current) {
        URL.revokeObjectURL(pdfUrlRef.current);
        pdfUrlRef.current = null;
      }
    };
  }, []);

  const fetchPurchaseLedger = useCallback(
    async ({ page = 1, limit = 10, silent = false, bypassCache = false } = {}) => {
      const cacheKey = entireReport ? `${page}:${limit}` : "";
      if (cacheKey && !bypassCache) {
        const cached = pageCacheRef.current.get(cacheKey);
        if (cached) {
          setLedgerRows(cached.ledgerRows);
          setSummaryData(cached.summaryData);
          setCurrentPage(cached.currentPage);
          setTotalCount(cached.totalCount);
          setHasMorePages(cached.hasMorePages);
          setLoading(false);
          return;
        }
      }

      setLoading(true);

      const apiPayload = {};

      if (entireReport) {
        apiPayload.type = "entire";
        apiPayload.page = page;
        apiPayload.limit = limit;
      } else {
        if (fromDate) apiPayload.from_date = fromDate;
        if (toDate) apiPayload.to_date = toDate;
      }

      try {
        const payload = await postPurchaseLedger(apiPayload);

        if (!payload || (!isErpApiSuccess(payload) && payload.message !== "radha")) {
          toast.error(
            payload?.message || "Failed to fetch ledger data. Please try again."
          );
          setLedgerRows([]);
          setSummaryData(null);
          setTotalCount(0);
          return;
        }

        const entries = payload.data?.entries || payload.data || [];

        if (!Array.isArray(entries) || entries.length === 0) {
          toast.error("No ledger entries found for the selected date range.");
          setLedgerRows([]);
          setSummaryData(null);
          setTotalCount(0);
          return;
        }

        const processedEntries = processPurchaseLedgerEntries(entries);
        const summary = summarizePurchaseLedgerRows(processedEntries);

        setLedgerRows(processedEntries);
        setSummaryData(summary);

        const paginationMeta = entireReport
          ? parseApiPagination(payload, processedEntries.length)
          : null;

        if (entireReport && paginationMeta) {
          setCurrentPage(paginationMeta.page);
          setTotalCount(paginationMeta.total);
          setHasMorePages(paginationMeta.hasMore);
          if (paginationMeta.limit) setItemsPerPage(paginationMeta.limit);
        } else {
          setCurrentPage(1);
          setTotalCount(processedEntries.length);
          setHasMorePages(false);
        }

        if (cacheKey) {
          pageCacheRef.current.set(cacheKey, {
            ledgerRows: processedEntries,
            summaryData: summary,
            currentPage: paginationMeta?.page || page,
            totalCount: paginationMeta?.total ?? processedEntries.length,
            hasMorePages: Boolean(paginationMeta?.hasMore),
          });
        }

        if (!silent) {
          toast.success("Ledger generated successfully!");
        }
      } catch (error) {
        toast.error(
          getErpRequestErrorMessage(
            error,
            "An error occurred while fetching the ledger data."
          )
        );
        console.error("Error fetching purchase ledger:", error);
        setLedgerRows([]);
        setSummaryData(null);
        setTotalCount(0);
      } finally {
        setLoading(false);
      }
    },
    [entireReport, fromDate, toDate],
  );

  const fetchRef = useRef(fetchPurchaseLedger);
  fetchRef.current = fetchPurchaseLedger;

  const initialFetchRef = useRef(false);
  // Fetch data only once on component mount
  useEffect(() => {
    if (initialFetchRef.current) return;
    initialFetchRef.current = true;
    fetchRef.current({ page: 1, limit: itemsPerPage });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Listen for API URL changes and refetch data
  useEffect(() => {
    const handleApiUrlChange = (event) => {
      console.log('API URL changed in PurchaseLedger, refetching ledger...', event.detail);
      pageCacheRef.current.clear();
      fetchRef.current({
        page: currentPage,
        limit: itemsPerPage,
        silent: true,
        bypassCache: true,
      });
    };

    window.addEventListener('apiUrlChanged', handleApiUrlChange);

    return () => {
      window.removeEventListener('apiUrlChanged', handleApiUrlChange);
    };
  }, [currentPage, itemsPerPage]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    pageCacheRef.current.clear();
    setCurrentPage(1);
    await fetchPurchaseLedger({
      page: 1,
      limit: itemsPerPage,
      bypassCache: true,
    });
  };

  const handlePageChange = (page) => {
    if (ignoreNextPageFetchRef.current) {
      ignoreNextPageFetchRef.current = false;
      return;
    }
    setCurrentPage(page);
    fetchPurchaseLedger({ page, limit: itemsPerPage, silent: true });
  };

  const handleItemsPerPageChange = (limit) => {
    ignoreNextPageFetchRef.current = true;
    pageCacheRef.current.clear();
    setItemsPerPage(limit);
    setCurrentPage(1);
    fetchPurchaseLedger({ page: 1, limit, silent: true, bypassCache: true });
  };

  const handlePrint = async () => {
    if (!ledgerRows.length && totalCount === 0) {
      toast.error("Please generate the ledger first.");
      return;
    }
    if (printLoading) return;

    setPrintLoading(true);

    const printPayload = {
      page: 0,
      limit: 0,
    };

    if (entireReport) {
      printPayload.type = "entire";
      printPayload.filter_type = "entire";
    } else {
      if (fromDate) printPayload.from_date = fromDate;
      if (toDate) printPayload.to_date = toDate;
    }

    try {
      const payload = await postPurchaseLedger(printPayload);

      if (
        !payload ||
        (!isErpApiSuccess(payload) && payload.message !== "radha")
      ) {
        toast.error(
          payload?.message || "Failed to fetch ledger data for printing. Please try again."
        );
        return;
      }

      const entries = payload.data?.entries || payload.data || [];

      if (!Array.isArray(entries) || entries.length === 0) {
        toast.error("No ledger entries found to print.");
        return;
      }

      const processedEntries = processPurchaseLedgerEntries(entries);
      const summary = summarizePurchaseLedgerRows(processedEntries);

      if (pdfUrlRef.current) {
        URL.revokeObjectURL(pdfUrlRef.current);
        pdfUrlRef.current = null;
      }

      const blob = await generatePurchaseLedgerPdfBlob({
        ledgerData: processedEntries,
        summaryData: summary,
      });

      const blobUrl = URL.createObjectURL(blob);
      pdfUrlRef.current = blobUrl;
      setPdfBlobUrl(blobUrl);
      setShowPDFModal(true);
    } catch (error) {
      toast.error(
        getErpRequestErrorMessage(
          error,
          "An error occurred while generating the print data."
        )
      );
      console.error("Error generating purchase ledger for print:", error);
    } finally {
      setPrintLoading(false);
    }
  };

  // Format currency with ₹ symbol and commas
  const formatCurrency = (value) => {
    if (value === 0) return "-";
    const absValue = Math.abs(value);
    return `₹ ${Math.round(absValue).toLocaleString("en-IN")}`;
  };

  // Format currency with decimals in Indian format
  const formatCurrencyDecimal = (value) => {
    const n = Number(value);
    if (!Number.isFinite(n) || n === 0) return "-";
    const absValue = Math.abs(n);
    return `₹ ${absValue.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  // Format weight with 3 decimal places
  const formatWeight = (value) => {
    if (value === 0) return "-";
    const absValue = Math.abs(value);
    return absValue.toFixed(3);
  };

  return (
    <div className="body-wrapper">
      <Toaster />
      <div className="container-fluid">
        <div className="card">
          <div className="border-bottom title-part-padding">
            <h4 className="card-title mb-0">Purchase Ledger</h4>
          </div>
          <div className="card-body">
            <form noValidate="" onSubmit={handleSubmit}>
              <div className="row align-items-end">
                {/* From Date */}
                <div className="mb-3 col-md-3">
                  <label className="form-label">From Date</label>
                  <input
                    type="date"
                    className={`form-control ${buttonClass}`}
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    disabled={entireReport}
                  />
                </div>
                {/* To Date */}
                <div className="mb-3 col-md-3">
                  <label className="form-label">To Date</label>
                  <input
                    type="date"
                    className={`form-control ${buttonClass}`}
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    disabled={entireReport}
                  />
                </div>
                {/* Entire Report Checkbox */}
                <div
                  className="mb-3 col-md-3 d-flex align-items-center"
                  style={{ marginTop: 32 }}
                >
                  <Form.Check
                    type="checkbox"
                    id="entireReportCheckbox"
                    label="Entire Report"
                    checked={entireReport}
                    onChange={(e) => setEntireReport(e.target.checked)}
                  />
                </div>
                {/* Submit Button */}
                <div
                  className="mb-3 col-md-3 d-flex align-items-center"
                  style={{ marginTop: 32 }}
                >
                  <button
                    type="submit"
                    className={`btn btn-primary w-100 ${buttonClass}`}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        />
                        Loading...
                      </>
                    ) : (
                      "Submit"
                    )}
                  </button>
                </div>
              </div>
            </form>
            {/* Ledger Table */}
            {(ledgerRows.length > 0 || (entireReport && totalCount > 0)) && (
              <div className="mt-4">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="mb-0">
                    Purchase
                    {entireReport && totalCount > 0 ? (
                      <span className="text-muted fs-6 ms-2">
                        (Page {currentPage} of{" "}
                        {Math.max(1, Math.ceil(totalCount / itemsPerPage))})
                      </span>
                    ) : null}
                  </h5>
                  <button
                    type="button"
                    className={`btn btn-secondary ${buttonClass}`}
                    onClick={handlePrint}
                    disabled={printLoading || loading}
                  >
                    {printLoading ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        />
                        Fetching records...
                      </>
                    ) : (
                      "Print Report"
                    )}
                  </button>
                </div>
                <div className={`ledger-report-wrap mb-4 ${cardClass}`}>
                  <div className="table-responsive">
                    <table className="table text-nowrap mb-0 align-middle ledger-report-table">
                    <thead>
                      <tr>
                        <th
                          colSpan="2"
                          className="text-center"
                        >
                          Supplier Details
                        </th>
                        <th
                          colSpan="3"
                          className="text-center"
                        >
                          PURCHASE DETAILS
                        </th>
                        <th
                          rowSpan="2"
                          className="text-end text-nowrap"
                        >
                          TAXABLE VALUE
                        </th>
                        <th
                          colSpan="3"
                          className="text-center"
                        >
                          AMOUNT OF TAX
                        </th>
                        <th
                          colSpan="2"
                          className="text-center"
                        >
                          WEIGHT
                        </th>
                      </tr>
                      <tr>
                        <th
                          className="text-nowrap"
                        >
                          NAME
                        </th>
                        <th
                          className="text-nowrap"
                        >
                          Phone
                        </th>
                        <th
                          className="text-nowrap"
                        >
                          PURCHASE NO
                        </th>
                        <th
                          className="text-nowrap"
                        >
                          PURCHASE DATE
                        </th>
                        <th
                          className="text-end text-nowrap"
                        >
                          PURCHASE VALUE
                        </th>
                        <th
                          className="text-end text-nowrap"
                        >
                          SGST
                        </th>
                        <th
                          className="text-end text-nowrap"
                        >
                          CGST
                        </th>
                        <th
                          className="text-end text-nowrap"
                        >
                          TOTAL TAX
                        </th>
                        <th
                          className="text-end text-nowrap"
                        >
                          GOLD (GM)
                        </th>
                        <th
                          className="text-end text-nowrap"
                        >
                          SILVER (GM)
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {ledgerRows.map((row, idx) => (
                        <tr key={idx}>
                          <td
                          >
                            {row.name}
                          </td>
                          <td
                          >
                            {row.phone}
                          </td>
                          <td
                          >
                            {row.purchase_no}
                          </td>
                          <td
                          >
                            {row.purchase_date}
                          </td>
                          <td
                            className="text-end"
                          >
                            {formatCurrency(row.purchase_value)}
                          </td>
                          <td
                            className="text-end"
                          >
                            {formatCurrencyDecimal(row.taxable_value)}
                          </td>
                          <td
                            className="text-end"
                          >
                            {formatCurrencyDecimal(row.sgst)}
                          </td>
                          <td
                            className="text-end"
                          >
                            {formatCurrencyDecimal(row.cgst)}
                          </td>
                          <td
                            className="text-end"
                          >
                            {formatCurrencyDecimal(row.total_tax)}
                          </td>
                          <td
                            className="text-end"
                          >
                            {formatWeight(row.gold_gm)}
                          </td>
                          <td
                            className="text-end"
                          >
                            {formatWeight(row.silver_gm)}
                          </td>
                        </tr>
                      ))}
                      {/* Summary Row */}
                      {summaryData && (
                        <tr className="ledger-report-summary">
                          <td
                            colSpan="4"
                          ></td>
                          <td
                            className="text-end"
                          >
                            {formatCurrency(summaryData.purchase_value)}
                          </td>
                          <td
                            className="text-end"
                          >
                            {formatCurrencyDecimal(summaryData.taxable_value)}
                          </td>
                          <td
                            className="text-end"
                          >
                            {formatCurrencyDecimal(summaryData.sgst)}
                          </td>
                          <td
                            className="text-end"
                          >
                            {formatCurrencyDecimal(summaryData.cgst)}
                          </td>
                          <td
                            className="text-end"
                          >
                            {formatCurrencyDecimal(summaryData.total_tax)}
                          </td>
                          <td
                            className="text-end"
                          >
                            {formatWeight(summaryData.gold_gm)}
                          </td>
                          <td
                            className="text-end"
                          >
                            {formatWeight(summaryData.silver_gm)}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                  </div>
                </div>
              </div>
            )}

            {entireReport && totalCount > 0 && (
              <Pagination
                totalItems={totalCount}
                hasMore={hasMorePages}
                itemsPerPage={itemsPerPage}
                currentPage={currentPage}
                onPageChange={handlePageChange}
                onItemsPerPageChange={handleItemsPerPageChange}
                itemsPerPageOptions={[10, 20, 30, 50, 100]}
              />
            )}
          </div>
        </div>
      </div>

      {/* Print Loading Modal */}
      <Modal
        show={printLoading}
        backdrop="static"
        keyboard={false}
        centered
        size="md"
      >
        <Modal.Body className="text-center p-4">
          <div
            className="spinner-border text-primary mb-3"
            style={{ width: "3rem", height: "3rem" }}
            role="status"
          >
            <span className="visually-hidden">Loading...</span>
          </div>
          <h5 className="mb-2">Preparing Purchase Ledger PDF</h5>
          <p className="text-muted mb-0" style={{ fontSize: "0.95rem" }}>
            This may take a few minutes. Thank you for your patience and understanding.
          </p>
        </Modal.Body>
      </Modal>

      {/* PDF Modal */}
      <Modal
        show={showPDFModal}
        onHide={() => {
          setShowPDFModal(false);
          if (pdfUrlRef.current) {
            URL.revokeObjectURL(pdfUrlRef.current);
            pdfUrlRef.current = null;
          }
          setPdfBlobUrl(null);
        }}
        size="xl"
        centered
        dialogClassName="modal-90w"
      >
        <Modal.Header closeButton>
          <Modal.Title>Purchase Ledger PDF Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ height: "80vh", padding: 0 }}>
          {pdfBlobUrl ? (
            <iframe
              title="Purchase Ledger PDF Preview"
              src={pdfBlobUrl}
              style={{ width: "100%", height: "100%", border: "none" }}
            />
          ) : (
            <div className="d-flex justify-content-center align-items-center h-100">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading PDF...</span>
              </div>
            </div>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
}

export default PurchaseLedger;
